import React, { useEffect, useMemo, useState } from 'react';
import homeService from '../../services/homeService';
import SectionsEditor, { normalizeByType } from './SectionsEditor';
import './dataEditor.css';

// Self-contained JsonEditor implementation and styles
const isObject = (v) => v && typeof v === 'object' && !Array.isArray(v);
const isArray = (v) => Array.isArray(v);
const extendedTypeOf = (v, hint) => {
  if (hint) return hint;
  if (isArray(v)) return 'array';
  if (isObject(v)) return 'object';
  if (typeof v === 'number' && isFinite(v)) return 'number';
  const s = v == null ? '' : String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return 'date';
  if (s.includes('\n') || s.length > 120) return 'textArea';
  return 'string';
};
const todayStr = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};
const defaultForType = (t) => {
  switch (t) {
    case 'array': return [];
    case 'object': return {};
    case 'number': return 0;
    case 'date': return todayStr();
    case 'textArea': return '';
    case 'string':
    default: return '';
  }
};
const convertValueToType = (val, t) => {
  switch (t) {
    case 'array': return Array.isArray(val) ? val : [];
    case 'object': return isObject(val) ? val : {};
    case 'number': {
      if (typeof val === 'number' && isFinite(val)) return val;
      const n = parseFloat(String(val ?? '').trim());
      return Number.isFinite(n) ? n : 0;
    }
    case 'date': {
      const s = String(val ?? '').trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
      const d = new Date(s);
      if (!isNaN(d.getTime())) {
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${mm}-${dd}`;
      }
      return todayStr();
    }
    case 'textArea':
    case 'string':
    default:
      return String(val ?? '');
  }
};
const deepClone = (v) => { try { return JSON.parse(JSON.stringify(v)); } catch (_) { return defaultForType(extendedTypeOf(v)); } };
const TypeSelector = ({ value, onChange, typeHint, disabled }) => {
  const t = extendedTypeOf(value, typeHint);
  return (
    <select className="form-select form-select-sm" value={t} onChange={(e) => onChange(convertValueToType(value, e.target.value))} disabled={!!typeHint || disabled}>
      <option value="string">string</option>
      <option value="textArea">textArea</option>
      <option value="number">number</option>
      <option value="date">date</option>
      <option value="array">array</option>
      <option value="object">object</option>
    </select>
  );
};
const StringEditor = ({ value, onChange }) => (
  <input type="text" className="form-control form-control-sm" value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value)} />
);
const TextAreaEditor = ({ value, onChange }) => (
  <textarea className="form-control form-control-sm" rows={4} value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value)} />
);
const NumberEditor = ({ value, onChange }) => (
  <input type="number" step="any" className="form-control form-control-sm" value={typeof value === 'number' && isFinite(value) ? value : 0} onChange={(e) => { const n = parseFloat(e.target.value); onChange(Number.isFinite(n) ? n : 0); }} />
);
const DateEditor = ({ value, onChange }) => {
  const toDateVal = (v) => { const s = String(v ?? '').trim(); return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : todayStr(); };
  return (<input type="date" className="form-control form-control-sm" value={toDateVal(value)} onChange={(e) => onChange(e.target.value)} />);
};
// Generic object editor (with type selector) — used only where schema is flexible
const ObjectEditor = ({ value, onChange, level = 0, fieldTypeHints, lockSchema }) => {
  const entries = Object.entries(value || {});
  const renameKey = (oldKey, newKey) => { if (lockSchema && level === 0) return; if (!newKey || newKey === oldKey) return; const exists = Object.prototype.hasOwnProperty.call(value, newKey); const safeKey = exists ? `${newKey}_1` : newKey; const next = { ...value }; next[safeKey] = next[oldKey]; delete next[oldKey]; onChange(next); };
  const updateChild = (key, nextVal) => { onChange({ ...value, [key]: nextVal }); };
  const removeKey = (key) => { if (lockSchema && level === 0) return; const next = { ...value }; delete next[key]; onChange(next); };
  const addField = () => { if (lockSchema && level === 0) return; let i = entries.length + 1; let key = `key${i}`; while (Object.prototype.hasOwnProperty.call(value, key)) { i += 1; key = `key${i}`; } onChange({ ...value, [key]: '' }); };
  return (
    <div className="json-object editor-block border rounded p-2 bg-light">
      {entries.length === 0 && <div className="text-muted small">(空對象)</div>}
      {entries.map(([k, v], idx) => (
        <div key={`${k}-${idx}`} className="json-field-row d-flex align-items-start gap-2 mb-2">
          <input className="form-control form-control-sm json-key-input" value={k} onChange={(e) => renameKey(k, e.target.value)} readOnly={lockSchema && level === 0} />
          <div className="json-type">
            <TypeSelector value={v} onChange={(nv) => updateChild(k, nv)} typeHint={fieldTypeHints ? fieldTypeHints[k] : undefined} />
          </div>
          <div className="json-value flex-grow-1">
            <ValueEditor value={v} onChange={(nv) => updateChild(k, nv)} level={level + 1} fieldTypeHints={undefined} lockSchema={lockSchema} typeHint={fieldTypeHints ? fieldTypeHints[k] : undefined} />
          </div>
          {!(lockSchema && level === 0) && (<button type="button" className="btn btn-sm btn-outline-danger json-delete-btn" onClick={() => removeKey(k)}>刪除</button>)}
        </div>
      ))}
      {!(lockSchema && level === 0) && (<button type="button" className="btn btn-sm btn-outline-primary json-add-btn" onClick={addField}>+ 新增字段</button>)}
    </div>
  );
};
// Generic array editor (with type selector) — not used for sections in Home editor
const ArrayEditor = ({ value, onChange, level = 0, fieldTypeHints, lockSchema }) => {
  const items = Array.isArray(value) ? value : [];
  const updateItem = (index, nextVal) => { const next = items.slice(); next[index] = nextVal; onChange(next); };
  const removeItem = (index) => { const next = items.slice(); next.splice(index, 1); onChange(next); };
  const addItem = () => { onChange([...(items || []), '']); };
  return (
    <div className="json-array editor-block border rounded p-2 bg-light">
      {items.length === 0 && <div className="text-muted small">(空數組)</div>}
      {items.map((it, idx) => (
        <div key={idx} className="json-item-row d-flex align-items-start gap-2 mb-2">
          <div className="json-type"><TypeSelector value={it} onChange={(nv) => updateItem(idx, nv)} /></div>
          <div className="json-value flex-grow-1"><ValueEditor value={it} onChange={(nv) => updateItem(idx, nv)} level={level + 1} fieldTypeHints={undefined} lockSchema={lockSchema} /></div>
          <button type="button" className="btn btn-sm btn-outline-danger json-delete-btn" onClick={() => removeItem(idx)}>刪除</button>
        </div>
      ))}
      <button type="button" className="btn btn-sm btn-outline-primary json-add-btn" onClick={addItem}>+ 新增項目</button>
    </div>
  );
};
const ValueEditor = ({ value, onChange, level = 0, fieldTypeHints, lockSchema, typeHint }) => {
  const t = extendedTypeOf(value, typeHint);
  if (t === 'object') return <ObjectEditor value={value} onChange={onChange} level={level} fieldTypeHints={fieldTypeHints} lockSchema={lockSchema} />;
  if (t === 'array') return <ArrayEditor value={value} onChange={onChange} level={level} fieldTypeHints={fieldTypeHints} lockSchema={lockSchema} />;
  if (t === 'number') return <NumberEditor value={value} onChange={onChange} />;
  if (t === 'date') return <DateEditor value={value} onChange={onChange} />;
  if (t === 'textArea') return <TextAreaEditor value={value} onChange={onChange} />;
  return <StringEditor value={value} onChange={onChange} />;
};

// Plain editors (no type dropdown anywhere)
const PlainArrayEditor = ({ value, onChange }) => {
  const items = Array.isArray(value) ? value : [];
  const updateItem = (index, nextVal) => { const next = items.slice(); next[index] = nextVal; onChange(next); };
  const removeItem = (index) => { const next = items.slice(); next.splice(index, 1); onChange(next); };
  const addItem = () => { onChange([...(items || []), '']); };
  return (
    <div className="json-array editor-block border rounded p-2 bg-light">
      {items.length === 0 && <div className="text-muted small">(空數組)</div>}
      {items.map((it, idx) => (
        <div key={idx} className="json-item-row d-flex align-items-start gap-2 mb-2">
          <div className="json-value flex-grow-1"><PlainValueEditor value={it} onChange={(nv) => updateItem(idx, nv)} /></div>
          <button type="button" className="btn btn-sm btn-outline-danger json-delete-btn" onClick={() => removeItem(idx)}>刪除</button>
        </div>
      ))}
      <button type="button" className="btn btn-sm btn-outline-primary json-add-btn" onClick={addItem}>+ 新增項目</button>
    </div>
  );
};

const PlainValueEditor = ({ value, onChange }) => {
  const t = extendedTypeOf(value);
  if (t === 'object') return <SimpleObjectEditor value={value} onChange={onChange} />;
  if (t === 'array') return <PlainArrayEditor value={value} onChange={onChange} />;
  if (t === 'number') return <NumberEditor value={value} onChange={onChange} />;
  if (t === 'date') return <DateEditor value={value} onChange={onChange} />;
  if (t === 'textArea') return <TextAreaEditor value={value} onChange={onChange} />;
  return <StringEditor value={value} onChange={onChange} />;
};

// Helpers for fixed Home editor UI
const camelToLabel = (k) => {
  if (!k) return '';
  const withSpaces = String(k)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-z])/g, '$1 $2')
    .trim();
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};

// A simplified object editor without type dropdowns (keys editable; values auto-infer; add/remove allowed)
const SimpleObjectEditor = ({ value, onChange }) => {
  const obj = isObject(value) ? value : {};
  const entries = Object.entries(obj);
  const renameKey = (oldKey, newKey) => {
    if (!newKey || newKey === oldKey) return;
    const next = { ...obj };
    if (Object.prototype.hasOwnProperty.call(next, newKey)) {
      // avoid collision
      newKey = `${newKey}_1`;
    }
    next[newKey] = next[oldKey];
    delete next[oldKey];
    onChange(next);
  };
  const updateChild = (key, nextVal) => onChange({ ...obj, [key]: nextVal });
  const removeKey = (key) => { const next = { ...obj }; delete next[key]; onChange(next); };
  const addField = () => {
    let i = entries.length + 1;
    let key = `key${i}`;
    while (Object.prototype.hasOwnProperty.call(obj, key)) { i += 1; key = `key${i}`; }
    onChange({ ...obj, [key]: '' });
  };
  return (
    <div className="json-object editor-block border rounded p-2 bg-light">
      {entries.length === 0 && <div className="text-muted small">(空對象)</div>}
      {entries.map(([k, v], idx) => (
        <div key={`${k}-${idx}`} className="json-field-row d-flex align-items-start gap-2 mb-2">
          <input className="form-control form-control-sm json-key-input" value={k} onChange={(e) => renameKey(k, e.target.value)} />
          <div className="json-value flex-grow-1">
            <PlainValueEditor value={v} onChange={(nv) => updateChild(k, nv)} />
          </div>
          <button type="button" className="btn btn-sm btn-outline-danger json-delete-btn" onClick={() => removeKey(k)}>Delete</button>
        </div>
      ))}
      <button type="button" className="btn btn-sm btn-outline-primary json-add-btn" onClick={addField}>+ Add field</button>
    </div>
  );
};

// Fixed hero editor: keys are title, subtitle, imageUrl (all strings)
const HeroEditor = ({ value, onChange }) => {
  const v = isObject(value) ? value : {};
  const safe = {
    title: String(v.title ?? ''),
    subtitle: String(v.subtitle ?? ''),
    imageUrl: String(v.imageUrl ?? ''),
  };
  const patch = (p) => onChange({ ...safe, ...p });
  return (
    <div className="json-object editor-block border rounded p-2 bg-light w-100">
      {[
        { key: 'title', label: '標題', value: safe.title, input: (val, set) => (<input type="text" className="form-control form-control-sm" value={val} onChange={(e) => set(e.target.value)} />) },
        { key: 'subtitle', label: '副標題', value: safe.subtitle, input: (val, set) => (<input type="text" className="form-control form-control-sm" value={val} onChange={(e) => set(e.target.value)} />) },
        { key: 'imageUrl', label: '圖片網址', value: safe.imageUrl, input: (val, set) => (<input type="text" className="form-control form-control-sm" value={val} onChange={(e) => set(e.target.value)} />) },
      ].map(({ key, label, value: val, input }) => (
        <div key={key} className="json-field-row d-flex align-items-start gap-2 mb-2">
          <span className="json-key-label">{label}</span>
          <div className="json-value flex-grow-1">
            {input(val, (nv) => patch({ [key]: nv }))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Sections editor: array of objects only; no type dropdown; allow add/remove items
// Now using shared SectionsEditor component

// Root fixed editor for Home: two fixed keys: hero (object) and sections (array)
const FixedHomeEditor = ({ value, onChange }) => {
  const v = isObject(value) ? value : {};
  const patch = (p) => onChange({ ...v, ...p });
  const [activeTab, setActiveTab] = useState('hero');
  
  return (
    <div className="json-object editor-block border rounded p-2 bg-light">
      {/* Tab Navigation */}
      <ul className="nav nav-tabs mb-3" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'hero' ? 'active' : ''}`}
            onClick={() => setActiveTab('hero')}
            type="button"
          >
            首頁橫幅
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'sections' ? 'active' : ''}`}
            onClick={() => setActiveTab('sections')}
            type="button"
          >
            區塊
          </button>
        </li>
      </ul>
      
      {/* Tab Content */}
      {activeTab === 'hero' && (
        <div className="w-100">
          <HeroEditor value={v.hero} onChange={(nv) => patch({ hero: nv })} />
        </div>
      )}
      {activeTab === 'sections' && (
        <div className="w-100">
          <SectionsEditor value={v.sections} onChange={(nv) => patch({ sections: nv })} />
        </div>
      )}
    </div>
  );
};
const LocalJsonEditor = ({ initialValue, onSave, fieldTypeHints, lockSchema = false }) => {
  const [value, setValue] = useState(() => deepClone(initialValue));
  const [savedOnce, setSavedOnce] = useState(false);
  useEffect(() => { setValue(deepClone(initialValue)); setSavedOnce(false); }, [initialValue]);
  
  // Check if form data has changed
  const hasChanges = useMemo(() => {
    return JSON.stringify(value) !== JSON.stringify(initialValue);
  }, [value, initialValue]);
  
  const save = async () => {
    try {
      if (onSave) await onSave(deepClone(value));
      setSavedOnce(true);
      if (typeof window !== 'undefined' && window.location) {
        window.location.reload();
      }
    } catch (_) { setSavedOnce(false); }
  };
  return (
    <div className="json-editor card shadow-sm mb-3">
      <div className="card-body">
        {/* Fixed Home schema editor: no root type selector, no add/delete at root, labels as text */}
        <FixedHomeEditor value={value} onChange={setValue} />
        <div className="d-flex justify-content-end align-items-center gap-2 mt-3 pt-3 border-top">
          {savedOnce && <div className="text-success small me-2">已儲存。</div>}
          <button 
            type="button" 
            className="btn btn-sm btn-primary" 
            onClick={save}
            disabled={!hasChanges}
          >
            儲存變更
          </button>
        </div>
      </div>
    </div>
  );
};

const HomeDataEditor = ({ filename }) => {
  const [data, setData] = useState({});
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const doc = await homeService.getObjectData();
        if (alive) setData(doc || {});
      } catch (_) { if (alive) setData({}); }
    })();
    return () => { alive = false; };
  }, []);
  const handleSave = async (next) => {
    const saved = await homeService.updateObjectData(next || {});
    setData(saved || {});
  };
  return (
    <LocalJsonEditor
      initialValue={data}
      onSave={handleSave}
    />
  );
};

export default HomeDataEditor;
